import { SensorType } from '@innovyze/shared-utils';
import { LoadingPage, WarningDialog } from '@innovyze/stylovyze';
import { FormikErrors, FormikProps } from 'formik';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import {
	checkOverridesValueMappingProfile,
	checkOverridesValueMappingProfilesRejected,
	checkSensorDeletionsValueMappingProfile,
	checkSensorDeletionsValueMappingProfilesRejected,
	deleteValuemappingProfile,
	getSingleValueMappingProfile,
	getValuemappingSensorsList,
	setValuemappingProfileDurationDialogOpen,
	updateValueMappingProfile,
} from '@Actions';
import {
	ValuemappingDurationDialog,
	ValuemappingOverridesWarningDialog,
	ValuemappingProfileCard,
} from '@Components';
import { useNavigateTo, useYupSchemaValidation } from '@Hooks';
import {
	createValuemappingProfileCreateRequest,
	generateInitialMappings,
} from '@Utils/valuemappingFormHelpers';
import {
	BodyGridStyled,
	PageWrapperStyled,
	StyledDeleteDiv,
} from './EditValuemappingProfile.styles';

import { ValuemappingProfileForm } from '@Components/ValuemappingProfileForm';
import ValuemappingProfileSchema from '@Pages/shared/ValuemappingProfileSchema';
import { getValue } from '@Selectors';
import { useGlobalization } from '@Translations';
import { MAIN_ROUTES } from '@Types/routes';
import { ValuemappingProfileFormShape } from '@Types/valuemappingForm.types';
import { determineOverridesMessage } from '@Utils/overridesMessage';
import ControlsButtons from './ControlsButtons';

const EditValuemappingProfile = () => {
	useNavigateTo();
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const history = useHistory();
	const { profileId } = useParams<{ profileId: string }>();
	const formRef = useRef<FormikProps<ValuemappingProfileFormShape>>();
	const [showDeleteProfileModal, setShowDeleteProfileModal] = useState(false);
	const [formErrors, setFormErrors] = useState<
		FormikErrors<ValuemappingProfileFormShape>
	>({});
	const [schemaValidate] =
		useYupSchemaValidation<ValuemappingProfileFormShape>();

	const {
		valueMappingProfiles: {
			initialized,
			valueMappingDetail,
			valueMappingDetail: {
				multiselect: {
					sensorIds: {
						options: sensorsList,
						initialized: sensorIdsListInitialized,
					},
				},
				deletableSensorsCount,
			},
			overrides: { list: overridesList },
			durationDialogOperation,
		},
	} = useSelector(getValue);

	useEffect(() => {
		dispatch(getValuemappingSensorsList());
		dispatch(checkOverridesValueMappingProfilesRejected());
		dispatch(checkSensorDeletionsValueMappingProfilesRejected());
	}, []);

	const overridesDialogMessages = useMemo(
		() =>
			determineOverridesMessage(
				overridesList,
				sensorsList,
				formRef.current?.values,
			),
		[overridesList],
	);

	useEffect(() => {
		dispatch(getSingleValueMappingProfile(profileId));
	}, [formRef]);

	useEffect(() => {
		if (
			deletableSensorsCount !== null &&
			Number.isInteger(deletableSensorsCount) &&
			deletableSensorsCount >= 0
		) {
			setShowDeleteProfileModal(true);
		} else {
			setShowDeleteProfileModal(false);
		}
	}, [deletableSensorsCount]);

	const handleClickOnBackButton = (): void => {
		history.push(MAIN_ROUTES.ValueMapping);
		dispatch(checkOverridesValueMappingProfilesRejected());
	};

	const handleClickOnButtonSave = (): void => {
		handleFormValidation();
	};

	const handleFormValidation = (): void => {
		if (!formRef.current) return;
		const validationResult = schemaValidate(
			formRef.current.values,
			ValuemappingProfileSchema(t),
		);
		if (validationResult.hasErrors) {
			setFormErrors(validationResult.errors as never);
		} else {
			setFormErrors({}); // ? unset errors
			handleCheckOverrides();
		}
	};

	const handleCheckOverrides = (): void => {
		if (!formRef.current) return;
		dispatch(
			checkOverridesValueMappingProfile({
				filterProfileIds: [profileId],
				body: createValuemappingProfileCreateRequest(
					formRef.current.values,
				),
				profileId: profileId,
			}),
		);
		dispatch(setValuemappingProfileDurationDialogOpen('NONE'));
	};

	const performUpdateOperation = () => {
		if (!formRef.current) return;
		const updateBody = createValuemappingProfileCreateRequest(
			formRef.current.values,
		);
		dispatch(
			updateValueMappingProfile({
				id: profileId,
				body: updateBody,
			}),
		);
	};

	const performDeleteOperation = () => {
		if (!formRef.current) return;
		dispatch(deleteValuemappingProfile(profileId));
	};

	const clickOnConfirmDurationDialogHandler =
		(deleteOp?: boolean) => (): void => {
			handleClickOnCancelButtonDurationDialog(); // ? closes dialog
			if (deleteOp) performDeleteOperation();
			else performUpdateOperation();
		};

	const handleClickOnDeleteButton = (): void => {
		if (!formRef.current) return;
		dispatch(
			checkSensorDeletionsValueMappingProfile({
				profileId: profileId,
				params: {
					types: formRef.current.values.sensorTypes as SensorType[],
					ids: formRef.current.values.sensorIds,
				},
			}),
		);
	};

	const handleClickOnCancelDeletionModal = (): void => {
		setShowDeleteProfileModal(false);
	};

	const handleDeleteProfile = () => {
		setShowDeleteProfileModal(false);
		dispatch(setValuemappingProfileDurationDialogOpen('DELETE'));
	};

	const handleClickOnCancelButtonOverridesDialog = (): void => {
		dispatch(checkOverridesValueMappingProfilesRejected());
	};

	const handleClickOnConfirmButtonOverridesDialog = (): void => {
		dispatch(setValuemappingProfileDurationDialogOpen('CREATE'));
	};

	const handleClickOnCancelButtonDurationDialog = (): void => {
		dispatch(checkOverridesValueMappingProfilesRejected());
		dispatch(setValuemappingProfileDurationDialogOpen('NONE'));
	};

	const initialMappings = useMemo(
		() => generateInitialMappings(valueMappingDetail.mappings),
		[valueMappingDetail.mappings],
	);

	return (
		<PageWrapperStyled
			title={t('Value Mapping')}
			secondary={
				<ControlsButtons
					disabled={!initialized}
					onClickCancelButton={handleClickOnBackButton}
					onClickSaveButton={handleClickOnButtonSave}
					onClickDeleteButton={handleClickOnDeleteButton}
				/>
			}
			isLoading={false}
			applyPadding
			headerUnderline>
			{!initialized && <LoadingPage />}
			<BodyGridStyled hidden={!initialized}>
				<ValuemappingProfileCard>
					<ValuemappingProfileForm
						formRef={formRef as unknown as never}
						errors={formErrors}
						disabledFields={{
							sensorIds: !sensorIdsListInitialized,
						}}
						initialValues={{
							profileName: valueMappingDetail.profileName,
							sensorTypes: valueMappingDetail.sensorTypes,
							sensorIds: valueMappingDetail.sensorIds,
							mappings: initialMappings,
						}}
						enableReinitialize
					/>
				</ValuemappingProfileCard>
			</BodyGridStyled>

			<WarningDialog
				title={t(
					'Delete profile from {{deletableSensorsCount}} sensors',
					{ deletableSensorsCount },
				)}
				open={showDeleteProfileModal}
				confirmText={t('Delete Profile')}
				cancelText={t('Cancel')}
				onConfirm={handleDeleteProfile}
				onCancel={handleClickOnCancelDeletionModal}>
				<StyledDeleteDiv>
					<div>
						{t(
							'{{deletableSensorsCount}} sensors will be affected.',
							{ deletableSensorsCount },
						)}
					</div>
					<div>{t('Do you want to continue with this action?')}</div>
				</StyledDeleteDiv>
			</WarningDialog>

			<ValuemappingOverridesWarningDialog
				open={!!overridesList.length}
				onConfirm={handleClickOnConfirmButtonOverridesDialog}
				onCancel={handleClickOnCancelButtonOverridesDialog}
				messages={overridesDialogMessages}
				confirmButtonDisabled={!initialized}
			/>

			<ValuemappingDurationDialog
				open={durationDialogOperation !== 'NONE'}
				onConfirm={clickOnConfirmDurationDialogHandler(
					durationDialogOperation === 'DELETE',
				)}
				onCancel={handleClickOnCancelButtonDurationDialog}
			/>
		</PageWrapperStyled>
	);
};

export default EditValuemappingProfile;
